import { AgencyId, type SuretyAccountId } from "@inrev/common";
import {
	useAdminFetchAgencyPreviews,
	useAdminFetchAgentPreviewsForAgency,
} from "@portal/api/agency/admin";
import { useFetchAccountPreviews } from "@portal/api/previews";
import { type FC, useEffect, useMemo, useRef } from "react";
import { type UseFormSetValue, useFormContext, useWatch } from "react-hook-form";
import { FormSearchableInputWithDropdown } from "../../../form/fields";
import { FormDropdown } from "../../../form/fields/FormDropdown";
import { Button } from "../../../primitives/Button";
import type { AgentOption } from "../BondRequestSubmissionAdapterContext";
import { ACCOUNT_FIELDS, SubmissionFormData } from "../bondRequestSubmissionFormSchema";
import { clearFieldsAfterAccount } from "../clearSubmissionFields";
import { StepFieldLabel } from "./StepFieldLabel";

type AgencyOption = { label: string; value: AgencyId };
type AccountOption = { label: string; value: SuretyAccountId };
const EMPTY_ACCOUNT_OPTIONS: AccountOption[] = [];
const EMPTY_AGENCY_OPTIONS: AgencyOption[] = [];
const EMPTY_AGENT_OPTIONS: AgentOption[] = [];

const useClearSelectionWhenMissing = <T extends string>(
	value: T | undefined,
	options: { value: T }[] | undefined,
	fieldName: "accountId" | "agencyId" | "agentId",
	setValue: UseFormSetValue<SubmissionFormData>,
) => {
	useEffect(() => {
		if (value && options && !options.some((option) => option.value === value)) {
			setValue(fieldName, undefined);
		}
	}, [value, options, fieldName, setValue]);
};

type AdminAccountAndAgencyStepProps = {
	onCancel: () => void;
	onNext: () => void;
};

export const AdminAccountAndAgencyStep: FC<AdminAccountAndAgencyStepProps> = ({
	onCancel,
	onNext,
}) => {
	const { control, setValue, trigger, getValues, clearErrors } =
		useFormContext<SubmissionFormData>();
	const accountId = useWatch({ control, name: "accountId" });
	const agencyId = useWatch({ control, name: "agencyId" });
	const agentId = useWatch({ control, name: "agentId" });

	const { accountPreviews, accountPreviewsIsLoading } = useFetchAccountPreviews();
	const { agencyPreviews, agencyPreviewsLoading } = useAdminFetchAgencyPreviews();
	const { agentPreviews, agentPreviewsLoading } = useAdminFetchAgentPreviewsForAgency(agencyId);

	const accountOptions = useMemo(
		() =>
			accountPreviews
				?.filter((account) => !agencyId || account.agencyId === agencyId)
				.map((account) => ({
					value: account.id,
					label: account.displayName ?? account.id,
				})),
		[accountPreviews, agencyId],
	);

	const agencyOptions = useMemo(
		() =>
			agencyPreviews?.map((agency) => ({
				value: agency.id,
				label: agency.displayName,
			})),
		[agencyPreviews],
	);

	const agentOptions = useMemo<AgentOption[] | undefined>(
		() =>
			agentPreviews?.map((agent) => ({
				label: `${agent.firstName} ${agent.lastName}`,
				value: agent.id,
			})),
		[agentPreviews],
	);

	const hasResolvedAccountRef = useRef(false);
	const previousAccountIdRef = useRef<SuretyAccountId | undefined>(undefined);
	useEffect(() => {
		const selectedAccount = accountId
			? accountPreviews?.find((account) => account.id === accountId)
			: undefined;
		// Don't wipe seeded values before previews resolve the account.
		if (accountId && !selectedAccount) return;

		if (!hasResolvedAccountRef.current) {
			hasResolvedAccountRef.current = true;
			previousAccountIdRef.current = accountId;
			if (selectedAccount?.agentId && !getValues("agentId")) {
				setValue("agentId", selectedAccount.agentId, {
					shouldValidate: true,
					shouldTouch: true,
					shouldDirty: true,
				});
			}
			return;
		}

		if (accountId === previousAccountIdRef.current) return;
		previousAccountIdRef.current = accountId;
		clearFieldsAfterAccount(setValue, clearErrors);
		if (!selectedAccount) return;
		setValue("agencyId", selectedAccount.agencyId, { shouldValidate: true, shouldDirty: true });
		if (selectedAccount.agentId) {
			setValue("agentId", selectedAccount.agentId, {
				shouldValidate: true,
				shouldTouch: true,
				shouldDirty: true,
			});
		}
	}, [accountId, accountPreviews, setValue, clearErrors, getValues]);

	useClearSelectionWhenMissing(accountId, accountOptions, "accountId", setValue);
	useClearSelectionWhenMissing(agencyId, agencyOptions, "agencyId", setValue);
	useClearSelectionWhenMissing(
		agentPreviewsLoading ? undefined : agentId,
		agentOptions,
		"agentId",
		setValue,
	);

	const agentsLoading = !!agencyId && agentPreviewsLoading;
	const isLoading = accountPreviewsIsLoading || agencyPreviewsLoading || agentsLoading;

	const handleNext = async () => {
		if ((await trigger([...ACCOUNT_FIELDS])) && getValues("accountId")) {
			onNext();
		}
	};

	return (
		<div className="flex flex-col gap-6 px-6 pt-6 pb-6">
			<div className="flex flex-col gap-2" data-testid="account-select">
				<StepFieldLabel required>Account</StepFieldLabel>
				<FormSearchableInputWithDropdown<SubmissionFormData>
					control={control}
					name="accountId"
					options={accountOptions ?? EMPTY_ACCOUNT_OPTIONS}
					anchor="bottom start"
				/>
			</div>
			<div className="flex flex-col gap-2" data-testid="agency-select">
				<StepFieldLabel required>Agency</StepFieldLabel>
				<FormSearchableInputWithDropdown<SubmissionFormData>
					control={control}
					name="agencyId"
					options={agencyOptions ?? EMPTY_AGENCY_OPTIONS}
					anchor="bottom start"
					readonly={!!accountId}
				/>
			</div>
			<div className="flex flex-col gap-2" data-testid="agent-select">
				<StepFieldLabel required>Agent</StepFieldLabel>
				<FormDropdown<SubmissionFormData>
					name="agentId"
					control={control}
					testId="agent-dropdown"
					options={agentOptions ?? EMPTY_AGENT_OPTIONS}
					placeholder={
						!agencyId
							? "Select an agency first"
							: agentsLoading
								? "Loading agents…"
								: "Select agent"
					}
					anchor="bottom start"
				/>
			</div>
			<div className="flex justify-end gap-2 pt-1">
				<Button color="secondary" onClick={onCancel}>
					Cancel
				</Button>
				<Button color="primary" onClick={handleNext} disabled={isLoading}>
					Next
				</Button>
			</div>
		</div>
	);
};
